import { createTheme, MantineColorsTuple, rem, virtualColor } from "@mantine/core"

/**
 * CloudSpry Mantine Theme Configuration
 * Following Mantine v7+ theming standards
 * @see https://mantine.dev/theming/theme-object/
 */

// Color tuples must have exactly 10 shades (indices 0-9)
// Mantine uses index 6 as the primary shade by default
const green: MantineColorsTuple = [
  "#f0fdf4", // 0 - lightest
  "#dcfce7", // 1
  "#bbf7d0", // 2
  "#86efac", // 3
  "#4ade80", // 4
  "#22c55e", // 5
  "#16a34a", // 6 - primary shade (default)
  "#15803d", // 7
  "#166534", // 8
  "#14532d", // 9 - darkest
]

const gray: MantineColorsTuple = [
  "#f9fafb", // 0
  "#f3f4f6", // 1
  "#e5e7eb", // 2
  "#d1d5db", // 3
  "#9ca3af", // 4
  "#6b7280", // 5
  "#4b5563", // 6
  "#374151", // 7
  "#1f2937", // 8
  "#111827", // 9
]

const red: MantineColorsTuple = [
  "#fef2f2", // 0
  "#fee2e2", // 1
  "#fecaca", // 2
  "#fca5a5", // 3
  "#f87171", // 4
  "#ef4444", // 5
  "#dc2626", // 6
  "#b91c1c", // 7
  "#991b1b", // 8
  "#7f1d1d", // 9
]

const yellow: MantineColorsTuple = [
  "#fffbeb", // 0
  "#fef3c7", // 1
  "#fde68a", // 2
  "#fcd34d", // 3
  "#fbbf24", // 4
  "#f59e0b", // 5
  "#d97706", // 6
  "#b45309", // 7
  "#92400e", // 8
  "#78350f", // 9
]

export const theme = createTheme({
  /** Primary color configuration */
  primaryColor: "green",
  primaryShade: { light: 6, dark: 4 },

  /** Color palette - only use Mantine's standard color names or custom ones */
  colors: {
    green,
    gray,
    red,
    yellow,
    // Virtual colors map to other colors based on color scheme
    error: virtualColor({
      name: "error",
      light: "red",
      dark: "red",
    }),
    warning: virtualColor({
      name: "warning",
      light: "yellow",
      dark: "yellow",
    }),
    success: virtualColor({
      name: "success",
      light: "green",
      dark: "green",
    }),
  },

  /** White and black colors */
  white: "#ffffff",
  black: "#0d1117",

  /** Typography */
  fontFamily: "var(--app-font, Inter, system-ui, -apple-system, sans-serif)",
  fontFamilyMonospace: "ui-monospace, SFMono-Regular, Consolas, monospace",
  headings: {
    fontFamily: "var(--app-font, Inter, system-ui, -apple-system, sans-serif)",
    fontWeight: "600",
    sizes: {
      h1: { fontSize: rem(34), lineHeight: "1.3" },
      h2: { fontSize: rem(26), lineHeight: "1.35" },
      h3: { fontSize: rem(22), lineHeight: "1.4" },
      h4: { fontSize: rem(18), lineHeight: "1.45" },
      h5: { fontSize: rem(16), lineHeight: "1.5" },
      h6: { fontSize: rem(14), lineHeight: "1.5" },
    },
  },

  /** Font sizes */
  fontSizes: {
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(18),
    xl: rem(20),
  },

  /** Line heights */
  lineHeights: {
    xs: "1.4",
    sm: "1.45",
    md: "1.55",
    lg: "1.6",
    xl: "1.65",
  },

  /** Spacing scale */
  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
  },

  /** Border radius scale */
  radius: {
    xs: rem(2),
    sm: rem(4),
    md: rem(6),
    lg: rem(8),
    xl: rem(12),
  },
  defaultRadius: "md",

  /** Shadow scale */
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  },

  /** Breakpoints - matches existing responsive design */
  breakpoints: {
    xs: "36em",
    sm: "48em",
    md: "62em",
    lg: "75em",
    xl: "88em",
  },

  /** Focus ring appearance */
  focusRing: "auto",

  /** Cursor type for interactive elements */
  cursorType: "pointer",

  /** Respect user's reduced motion preference */
  respectReducedMotion: true,

  /** Component default props and styles */
  components: {
    Button: {
      defaultProps: {
        radius: "md",
      },
      styles: {
        loader: {
          transform: "translate(-50%, -50%)",
        },
      },
    },

    Card: {
      defaultProps: {
        radius: "lg",
        withBorder: true,
        padding: "lg",
      },
    },

    Paper: {
      defaultProps: {
        radius: "md",
        p: "md",
      },
    },

    Input: {
      defaultProps: {
        radius: "md",
      },
    },

    TextInput: {
      defaultProps: {
        radius: "md",
      },
    },

    Textarea: {
      defaultProps: {
        radius: "md",
      },
    },

    Select: {
      defaultProps: {
        radius: "md",
      },
    },

    Modal: {
      defaultProps: {
        radius: "lg",
        centered: true,
        overlayProps: {
          backgroundOpacity: 0.55,
          blur: 3,
        },
      },
    },

    // Modal.Content renders a Paper internally, which inherits the global `p: "md"`
    // default above. That padding lands on Content itself (the scrollable element),
    // leaving a gap above the sticky Header where scrolled body content shows through.
    // Header and Body already carry their own padding, so Content needs none. This must
    // be a defaultProps override (a real `p` prop passed through to Paper), not a
    // `styles` override — Paper's padding is applied as an inline style, which a
    // stylesheet-based `styles` rule cannot outrank.
    ModalContent: {
      defaultProps: {
        p: 0,
      },
    },

    Drawer: {
      defaultProps: {
        overlayProps: {
          backgroundOpacity: 0.55,
          blur: 3,
        },
      },
    },

    ActionIcon: {
      defaultProps: {
        radius: "md",
      },
    },

    Badge: {
      defaultProps: {
        radius: "sm",
      },
    },

    Notification: {
      defaultProps: {
        radius: "md",
      },
    },

    Tooltip: {
      defaultProps: {
        radius: "sm",
      },
    },
  },

  /** Other settings */
  other: {
    // Custom properties that can be accessed via theme.other
    darkBg: "#0d1117",
    darkSurface: "#161b22",
    darkBorder: "#30363d",
  },
})

export default theme
